import {useFormSubmission} from '../../contexts/form-submission-context';
import {useMemo} from 'react';
import { useLanguage } from '@shondalai/ui-commons';
import { useFormTranslation } from '../../hooks/useFormTranslations';

export function FormHeader() {
  const { form } = useFormSubmission();
  const { t } = useLanguage();

  // Translate form title and description
  const translatedForm = useFormTranslation(
    form?.id || 0,
    { title: form?.title || '', description: form?.description }
  );

  if (!form) return null;

  // Get header style from theme
  const headerStyle = useMemo(() => {
    if (!form?.theme?.theme?.variables) return undefined;

    const vars = form.theme.theme.variables;
    const customVars = form.theme.customizations || {};

    // Check customizations first (user overrides)
    const headerGradient = customVars.headerGradient !== undefined
      ? customVars.headerGradient
      : vars.headerGradient;
    const headerImage = customVars.headerImage !== undefined
      ? customVars.headerImage
      : vars.headerImage;

    // If explicitly set to null, no header
    if (headerGradient === null && headerImage === null) {
      return null; // Explicitly disabled
    }

    // Prefer image over gradient if both are set
    if (headerImage) {
      return {
        backgroundImage: `url(${headerImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }

    if (headerGradient) {
      return { background: headerGradient };
    }

    return undefined; // No header configured
  }, [form?.theme]);

  const hasHeader = !!headerStyle;

  // Get border radius from theme for matching container corners
  const borderRadius = useMemo(() => {
    if (!form?.theme?.theme?.variables) return '16px';
    const vars = form.theme.theme.variables;
    const customVars = form.theme.customizations || {};
    const br = customVars.borderRadius || vars.borderRadius || '8px';
    // Container uses borderRadius * 2
    const numValue = parseFloat(br);
    return `${numValue * 2}px`;
  }, [form?.theme]);

  return (
    <>
      {hasHeader ? (
        // Header with gradient/image - title and description overlaid
        <div
          className="easyforms-form-header-banner relative overflow-hidden mb-6 md:mb-8 h-48 md:h-56 lg:h-64"
          style={{
            ...headerStyle,
            borderTopLeftRadius: borderRadius,
            borderTopRightRadius: borderRadius,
            marginLeft: 'calc(-1 * var(--ef-containerPadding, 3rem))',
            marginRight: 'calc(-1 * var(--ef-containerPadding, 3rem))',
            marginTop: 'calc(-1 * var(--ef-containerPadding, 3rem))',
          }}
        >
          {/* Gradient overlay for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />

          {/* Header content overlaid on gradient */}
          <div className="relative z-10 h-full flex flex-col justify-end p-5 md:p-8 lg:p-10 pb-6 md:pb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3 drop-shadow-lg">
              {translatedForm.title}
            </h1>

            {translatedForm.description && (
              <p className="text-sm md:text-base text-white/90 max-w-3xl drop-shadow-md">
                {translatedForm.description}
              </p>
            )}

            <p className="text-[11px] text-white/75 mt-3 drop-shadow">
              <span className="text-white/90">*</span> {t('COM_EASYFORMS_REQUIRED_FIELDS', 'Required fields')}
            </p>
          </div>
        </div>
      ) : (
        // No header - standard layout
        <div className="easyforms-form-header">
          <h1 className="easyforms-form-title">
            {translatedForm.title}
          </h1>

          {translatedForm.description && (
            <p className="easyforms-form-description">
              {translatedForm.description}
            </p>
          )}

          <p className="text-[11px] opacity-70 mt-2">
            <span className="easyforms-field-required">*</span> {t('COM_EASYFORMS_REQUIRED_FIELDS', 'Required fields')}
          </p>
        </div>
      )}
    </>
  );
}
